import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, provide, computed, toRaw, onMounted, onUnmounted } from 'vue';
import {
    ILocationQueryService, LocationModel, LocationQueryOptions, MeasureUnit, HoursOfOperationHolidaysCondition,
    ILocationEditManager, ILocationManager, CreateLocationModel, HoursOfOperationType, LocationHours, Address, LocationType, Listing,
    ListingTemplate
} from '@truckdown/locations';
import { locationKey, locationUpdateKey } from './keys';
import { applyAllGeneralEdits, applyAllMarketingEdits, hasMarketingEdit, showMessage } from "../utilities";
import editHeader from './EditHeader/EditHeader.vue';
import viewSelection from './ViewSelection/ViewSelection.vue';
import editFooter from './EditFooter/EditFooter.vue';
import accountLookup from '../AccountLookup/AccountLookup.vue';
import { ApiResultModel } from '@truckdown/systems';

interface IListingViewModel {
    listingTemplateType: ListingTemplate | undefined,
    overrideDefault: boolean,
    selectedSite: string
};

interface IData {
    showGeneralEdit: Ref<boolean>,
    showMarketingEdit: Ref<boolean>,
    showHoursEdit: Ref<boolean>,
    showDeclineModal: Ref<boolean>,
    showApproveModal: Ref<boolean>,
    showAccountModal: Ref<boolean>,
    applyMarketingEdits: Ref<boolean>,
    applyGeneralEdits: Ref<boolean>,
    editStep: Ref<number>,
    editTab: Ref<string>,
    selectedAccountId: Ref<string | undefined>,
    editLocation: Ref<LocationModel>,
    original: Ref<LocationModel>,
    listingValue: Ref<Listing>,
    overrideDefaultSite: Ref<boolean>,
    listingTemplateType: Ref<ListingTemplate>,
    createState: Ref<boolean>,
    editState: Ref<boolean>,
    viewState: Ref<boolean>,
    showViewSelection: Ref<boolean>,
    selectedSite: Ref<string>,
    showSection: Ref<string>,
    createLocationModel: Ref<CreateLocationModel | undefined>
}

const getInitialData = function (props: any): IData {
    return {
        showGeneralEdit: ref<boolean>(false),
        showMarketingEdit: ref<boolean>(false),
        showHoursEdit: ref<boolean>(false),
        showDeclineModal: ref<boolean>(false),
        showApproveModal: ref<boolean>(false),
        showAccountModal: ref<boolean>(false),
        applyMarketingEdits: ref<boolean>(hasMarketingEdit(props.location, context.system)),
        applyGeneralEdits: ref<boolean>(!!props.location.edit),
        editStep: ref<number>(1),
        editTab: ref<string>('services'),
        selectedAccountId: ref<string | undefined>(undefined),
        editLocation: ref<LocationModel>(props.location),
        original: ref<LocationModel>(structuredClone(toRaw(props.location))),
        listingValue: ref<Listing>(structuredClone(toRaw(props.listing))),
        overrideDefaultSite: ref<boolean>(false),
        listingTemplateType: ref<ListingTemplate>(props.listing.properties.template),
        createState: ref<boolean>(false),
        editState: ref<boolean>(false),
        viewState: ref<boolean>(false),
        showViewSelection: ref<boolean>(false),
        selectedSite: ref<string>(context.system),
        showSection: ref<string>(""),
        createLocationModel: ref<CreateLocationModel | undefined>()
    };
}

const getComputed = function (props: any, data: IData) {
    return {
        hasPendingEdits: computed(() => {
            const editLocation = data.editLocation.value;
            if (!editLocation) return false;

            if (editLocation.edit) return true;

            const details = editLocation.details ?? {};
            const hasEmptyKey = Object.prototype.hasOwnProperty.call(details, "");

            const detailToCheck = hasEmptyKey
                ? details[""]
                : details[data.selectedSite.value];

            return !!detailToCheck?.edit;
        }),
        searchUrl: computed(() => {
            const site = data.selectedSite.value;
            const regionCode = data.editLocation.value.address?.regionCode || '';
            const countryCode = data.editLocation.value.address?.countryCode || '';
            const city = data.editLocation.value.address?.city || '';
            const coordinates = data.editLocation.value.address?.coordinates || '';
            const locId = data.editLocation.value.id;
            const searchServices = data.editLocation.value.subscriptions[site] ? data.editLocation.value.subscriptions[site].services.join("-") : "";
            if (searchServices) {
                return `/search/results/${searchServices}/${city}%2C%20${regionCode}?lat=${coordinates.lat}&lng=${coordinates.lng}&svs=${searchServices}&rgnCd=${regionCode}&ctryCd=${countryCode}#${locId}`;
            }
            return "";
        }),
        createUrl: computed(() => {
            const adminSegment = props.isAdmin ? '/admin' : '';
            return `/listings${adminSegment}/view-location/${encodeURIComponent(data.editLocation.value.id)}#create`;
        }),
    };
}


const updateNavigation = function (hash: string) {
    let navItem = ""
    switch (hash) {
        case "#edit-hours":
            navItem = "Listing Hours";
            break;
        default:
            navItem = "Listings";
            break;
    }
    if (navItem) {
        context.events.emit('set-location-sub-item', navItem);
    }
}

const getMethods = function (props: any, data: IData, ctx: any) {
    async function declineGeneralEdits() {
        const manager = await context.getService<ILocationEditManager>('ILocationEditManager');
        try {
            if (data.editLocation.value.id) {
                let result = await manager.removeAllLocationEdits(props.listing.id);
                if (result && result.ok) {
                    return true;
                } else {
                    return false;
                }
            }
        } catch (error) {
            console.error('Decline All Location Edits Failed:', error);
        }
        return false;
    }
    async function declineMarketingEdits() {
        const manager = await context.getService<ILocationEditManager>('ILocationEditManager');
        try {
            if (data.editLocation.value) {
                let result = await manager.removeAllMarketingEdits(props.listing.id);
                if (result && result.ok) {
                    return true;
                } else {
                    return false;
                }
            }
        } catch (error) {
            console.error('Decline All Marketing Edits Failed:', error);
        }
        return false;
    }

    async function loadLocation() {
        try {
            const manager = await context.getService<ILocationQueryService>('ILocationQueryService');
            const result = await manager.findLocation(data.editLocation.value.id, {
                includePhoneNumbers: true,
                includeHoursOfOperation: true,
                includeMarketingData: true,
                system: context.system,
                includeNotes: false,
                includePreference: false,
                preferenceServices: [],
                measureUnit: MeasureUnit.Metric
            } as LocationQueryOptions);

            if (result) {
                data.editLocation.value = JSON.parse(JSON.stringify(result));
            }
        } catch (ex) {
            console.error(ex);
        }
    };

    async function loadListing() {
        try {
            const manager = await context.getService<ILocationQueryService>('ILocationQueryService');
            const result = await manager.findListing(data.editLocation.value.id, {
                includePhoneNumbers: true,
                includeHoursOfOperation: true,
                includeMarketingData: true,
                system: context.system,
                includeNotes: false,
                includePreference: false,
                preferenceServices: [],
                measureUnit: MeasureUnit.Metric
            } as LocationQueryOptions);

            if (result) {
                data.listingValue.value = result;
            }
        } catch (ex) {
            console.error(ex);
        }
    };

    return {
        openEdit: function (section: string, step?: number, tab?: string) {
            data.editStep.value = step || 1;
            data.editTab.value = tab || 'services';
            data.showSection.value = tab || "";

            switch (section) {
                case 'general':
                    data.showGeneralEdit.value = true;
                    data.showMarketingEdit.value = false;
                    data.showHoursEdit.value = false;
                    window.location.hash = "edit";
                    break;
                case 'marketing':
                    data.showMarketingEdit.value = true;
                    data.showHoursEdit.value = false;
                    data.showGeneralEdit.value = false;
                    window.location.hash = "edit";
                    break;
                case 'hours':
                    data.showHoursEdit.value = true;
                    data.showGeneralEdit.value = false;
                    data.showMarketingEdit.value = false;
                    window.location.hash = "edit-hours";
                    break;
            }
        },
        updateHours: async function () {
            //data.listingValue.value.hoursData = hours;
            await loadListing();
        },
        updateSites: function (site: string, override: boolean) {
            data.selectedSite.value = site;
            data.overrideDefaultSite.value = override;
        },
        discardChanges: function () {
            data.editLocation.value = structuredClone(toRaw(data.original.value));
        },
        confirmApprove: async function () {
            if (data.applyGeneralEdits.value && !!data.editLocation.value?.edit) {
                let applyListingEditsOk = await applyAllGeneralEdits(data.editLocation.value.id);
                if (!applyListingEditsOk) {
                    showMessage(`There was an error applying listing edits.`, "error", true, true);
                    return;
                }
            }

            if (data.applyMarketingEdits.value) {
                const applyMarketingEditsOk = await applyAllMarketingEdits(data.editLocation.value.id);
                if (!applyMarketingEditsOk) {
                    showMessage(`There was an error applying marketing edits.`, "succes", true, true);
                    return;
                }
            }

            showMessage(`Listing edits were successfully applied.`, "success", true, true);
            this.closeApproveModal();
            await loadLocation();
        },
        confirmDecline: async function () {
            if (data.applyGeneralEdits.value && !!data.editLocation.value?.edit) {
                let declineListingEditsOk = await declineGeneralEdits();
                if (!declineListingEditsOk) {
                    showMessage(`There was an error removing listing edits.`, "error", true, true);
                    return;
                }
            }

            if (data.applyMarketingEdits.value) {
                const details = data.editLocation.value?.details ?? {};
                const hasEmptyKey = Object.prototype.hasOwnProperty.call(details, "");
                const detailToCheck = hasEmptyKey ? details[""] : details[data.selectedSite.value];

                if (!!detailToCheck?.edit) {
                    const declineMarketingEditsOk = await declineMarketingEdits();
                    if (!declineMarketingEditsOk) {
                        showMessage(`There was an error removing marketing edits.`, "error", true, true);
                        return;
                    }
                }
            }

            showMessage(`Listing edits were successfully removed.`, "success", true, true);
            this.closeDeclineModal();
            await loadLocation();
        },
        closeModals: function () {
            data.createState.value = false;
            data.showMarketingEdit.value = false;
            data.showHoursEdit.value = false;
            data.showGeneralEdit.value = false;
        },
        openApproveModal: function () {
            data.showApproveModal.value = true;
        },
        openAccountModal: function () {
            data.showAccountModal.value = true;
        },
        closeAccountModal: function () {
            data.showAccountModal.value = false;
        },
        openDeclineModal: function () {
            data.showDeclineModal.value = true;
        },
        closeDeclineModal: function () {
            data.showDeclineModal.value = false;
        },
        closeApproveModal: function () {
            data.showApproveModal.value = false;
        },
        toggleViewSelection: function () {
            data.showViewSelection.value = !data.showViewSelection.value;
        },
        updateLocation: async function () {
            await loadLocation();
        },
        showManageHours: function () {
            window.location.hash = "edit-hours";
        },
        hideManageHours: function () {
            window.location.hash = "edit";
        },
        createLocation: async function (newLocationId: string | undefined) {
            if (newLocationId) {
                window.location.href = props.isAdmin ? `/listings/admin/view-location/${encodeURIComponent(newLocationId)}#edit` : `/listings/view-location/${encodeURIComponent(newLocationId)}#edit`;
                showMessage(`Listing was successfully created.`, "success", true, true);
            } else {
                showMessage(`There was an error creating listing.`, "danger", true, true);
                if (!props.isAdmin) {
                    window.location.href = "/listings";
                } else if (props.isAdmin && props.accountData?.ain) {
                    window.location.href = `/listings/admin/${props.accountData.ain}`;
                }
            }
        },
        updateView: function (viewObject: IListingViewModel) {
            data.selectedSite.value = viewObject.selectedSite;
            data.listingTemplateType.value = viewObject.listingTemplateType ?? data.listingTemplateType.value;
            data.overrideDefaultSite.value = viewObject.overrideDefault;
        },
        changeMode: function (mode: string) {
            let hash = 'edit';
           
            if (mode == "edit") {
                data.editState.value = true;
                data.viewState.value = false;
            } else {
                data.viewState.value = true;
                data.editState.value = false;
                hash = '';
            }
            data.showHoursEdit.value = false;
            
            const { origin, pathname, search } = window.location;
            const newUrl = `${origin}${pathname}${search}${hash ? `#${hash}` : ''}`;
            history.pushState(null, '', newUrl);
            updateNavigation(hash);
        },
        selectAccount: function (account: any) {
            data.selectedAccountId.value = account?.value;
        },
        confirmMove: async function () {
            try {
                const manager = await context.getService<ILocationManager>('ILocationManager');
                const result = await manager.transferLocation({
                    locationId: data.editLocation.value.id,
                    newAccountId: data.selectedAccountId.value || ""
                });

                if (result && result.ok) {
                    data.showAccountModal.value = false;
                    window.location.href = `/listings/admin/${props.accountData.ain}`;
                    showMessage(`Listing was successfully moved.`, "success", true, true);

                } else {
                    showMessage(result?.message || `There was an error moving the listing to another account. Make sure listing has no active subscriptions.`, "danger", true, true);
                }
            } catch (ex) {
                console.error(ex);
            }
        }
    };
}

export default defineComponent({
    name: 'EditListing',
    props: {
        location: {
            type: Object as () => LocationModel,
            required: false
        },
        listing: {
            type: Object as () => Listing,
            required: false
        },
        allServices: {
            type: Array,
            required: true,
            default: []
        },
        allRegions: {
            type: Array,
            required: true,
            default: []
        },
        isAdmin: {
            type: Boolean,
            default: false,
            required: false
        },
        accountData: {
            type: Object as () => any,
            required: true
        }
    },

    components: {
        'edit-header': editHeader,
        'edit-footer': editFooter,
        'view-selection': viewSelection,
        'account-lookup': accountLookup
    },
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let computedValues = getComputed(props, data);
        let methods = getMethods(props, data, ctx);
        provide(locationKey, data.editLocation);
        provide(locationUpdateKey, updateLocation);

        function updateLocation(value: LocationModel) {
            data.editLocation.value = value;
            data.applyMarketingEdits.value = hasMarketingEdit(data.editLocation.value, data.selectedSite.value);
            data.applyGeneralEdits.value = !!data.editLocation.value.edit;
        }
        const setCreateLocationModel = function () {
            let locationHours = {
                id: "",
                timezone: "",
                type: HoursOfOperationType.Unknown,
                majorHolidayCondition: HoursOfOperationHolidaysCondition.RegularHours,
                holidays: {},
                entries: {},
                exceptions: [],
            } as LocationHours;

            let newLocationModel = {
                accountNumber: props.accountData.ain || 0,
                name: props.accountData.n || "",
                website: props.accountData.info.website || "",
                email: props.accountData.info.email || "",
                address: props.accountData.info.address || {} as Address,
                receiveNewsletter: true,
                office: props.accountData.info.officeNumber || "",
                fax: props.accountData.info.faxNumber || "",
                type: LocationType.Shop,
                services: [],
                amenities: [],
                features: [],
                acceptedPaymentTypes: [],
                hoursOfOperation: locationHours
            } as CreateLocationModel;
            data.createLocationModel.value = { ...newLocationModel }
        }

        const scrollToTop = () => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        };

        const processHash = async function () {
            let hash = window.location.hash;
            updateNavigation(hash);
            if (hash && hash.length > 0) {
                scrollToTop();
                data.createState.value = (hash == "#create");
                data.editState.value = (hash == "#edit");
                data.showHoursEdit.value = (hash == "#edit-hours");
                data.viewState.value = false;
                
                if (data.createState.value && !props.location?.id) {
                    setCreateLocationModel();
                }
            } else {
                data.viewState.value = true;
                data.createState.value = false;
                data.editState.value = false;
                data.showHoursEdit.value = false;
            }
            
        }

        onMounted(async () => {
            await processHash();
            window.addEventListener('hashchange', processHash);
        });

        onUnmounted(() => {
            window.removeEventListener('hashchange', processHash);
        });

        return {
            ...data,
            ...computedValues,
            ...methods,
        };
    }
});